/**
 * Publication policy for the blog catalog.
 *
 * The whole catalog is public. These 111 articles are already indexed and
 * ranking, so narrowing the public surface to a reviewed subset would 404 every
 * live URL that is not on the list — including the guides that currently earn
 * the site's only head-term positions. Editorial review continues against the
 * live catalog instead of behind a gate.
 *
 * To stage articles again later, flip `PUBLISH_ENTIRE_CATALOG` to false and put
 * the approved slugs in `REVIEWED_BLOG_SLUGS`. Everything downstream reads
 * through the helpers below, so that switch is the only edit required — but
 * retiring a live URL needs a redirect, not just a removal from the list.
 */
export const PUBLISH_ENTIRE_CATALOG = true

/**
 * Articles that have completed source and editorial review. This drives
 * featured placement on the blog index; it does not restrict what is public
 * while `PUBLISH_ENTIRE_CATALOG` is true.
 */
export const REVIEWED_BLOG_SLUGS = [
  'stripe-donations-without-writing-code',
  'how-to-add-a-stripe-donation-form-to-wordpress-with-the-kindlumen-plugin',
] as const

export type ReviewedBlogSlug = (typeof REVIEWED_BLOG_SLUGS)[number]

export const REVIEWED_BLOG_SLUG_SET: ReadonlySet<string> = new Set(REVIEWED_BLOG_SLUGS)

export function isReviewedBlogSlug(slug: string): slug is ReviewedBlogSlug {
  return REVIEWED_BLOG_SLUG_SET.has(slug)
}

export function isPubliclyPublishedBlogSlug(slug: string): boolean {
  return PUBLISH_ENTIRE_CATALOG || isReviewedBlogSlug(slug)
}

export function filterPubliclyPublishedBlogEntries<T extends { slug: string }>(
  entries: readonly T[],
): T[] {
  return PUBLISH_ENTIRE_CATALOG
    ? [...entries]
    : entries.filter((entry) => isPubliclyPublishedBlogSlug(entry.slug))
}

function getRelativeBlogSlug(href: string): string | null {
  const match = href.match(/^\/blog\/([^/?#]+)\/?(?:[?#].*)?$/)
  return match?.[1] ?? null
}

export function isPubliclyPublishedBlogHref(href: string): boolean {
  const slug = getRelativeBlogSlug(href)
  return slug !== null && isPubliclyPublishedBlogSlug(slug)
}

/**
 * Preserve useful anchor text while removing links to articles that have not
 * passed the publication gate. The authored catalog itself remains unchanged.
 */
export function unlinkUnpublishedBlogArticles(contentHtml: string): string {
  if (PUBLISH_ENTIRE_CATALOG) {
    return contentHtml
  }

  return contentHtml.replace(
    /<a\b([^>]*\bhref=(['"]))\/blog\/([^/\s"'?#]+)\/?(?:[?#][^"']*)?\2([^>]*)>([\s\S]*?)<\/a>/gi,
    (anchor, _beforeHref: string, _quote: string, slug: string, _afterHref: string, label: string) =>
      isPubliclyPublishedBlogSlug(slug) ? anchor : label,
  )
}
